import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionWarning } from '../../stores/sessionWarning';
import { useUserStore } from '../../stores/user';

// Banner NO bloqueante que avisa al alumno cuando su sesión está por expirar
// (por defecto, 15 min antes; lo enciende el sessionTimer). Trae un botón para
// renovar la sesión en silencio —sin re-loguear ni recargar— para no perder el
// avance de un examen en curso. Se apaga solo al renovar, cerrar sesión o expirar.
export const SessionExpiryBanner = () => {
	const { t } = useTranslation('Common');
	const [active, expiresAt, hide] = useSessionWarning((s) => [
		s.active,
		s.expiresAt,
		s.hide,
	]);
	const [isLogged, renewSession] = useUserStore((s) => [
		s.isLogged,
		s.renewSession,
	]);

	const [remainingMs, setRemainingMs] = React.useState(0);
	const [renewing, setRenewing] = React.useState(false);
	const [failed, setFailed] = React.useState(false);

	const visible = active && isLogged && !!expiresAt;

	// Conteo en vivo del tiempo restante. Solo corre mientras el banner está
	// visible; si llega a 0 lo resuelve el timeout de expiración del sessionTimer.
	React.useEffect(() => {
		if (!visible || !expiresAt) return;

		const tick = () =>
			setRemainingMs(Math.max(0, expiresAt * 1000 - Date.now()));
		tick();
		const id = setInterval(tick, 1000);
		return () => clearInterval(id);
	}, [visible, expiresAt]);

	if (!visible) return null;

	const totalSeconds = Math.floor(remainingMs / 1000);
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;
	const timeLabel = `${minutes}:${String(seconds).padStart(2, '0')}`;

	const handleRenew = async () => {
		setRenewing(true);
		setFailed(false);
		try {
			const exp = await renewSession();
			if (!exp) throw new Error('renew-failed');
			// Éxito: el sessionTimer re-arma el aviso a futuro y apaga el banner.
		} catch {
			setFailed(true);
		} finally {
			setRenewing(false);
		}
	};

	return (
		<div
			role="alert"
			aria-live="assertive"
			style={{
				position: 'fixed',
				top: 0,
				left: 0,
				right: 0,
				zIndex: 2000,
				display: 'flex',
				flexWrap: 'wrap',
				alignItems: 'center',
				justifyContent: 'center',
				gap: '0.75rem',
				padding: '0.75rem 1rem',
				background: '#fff3cd',
				color: '#664d03',
				borderBottom: '1px solid #ffe69c',
				boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
				fontSize: '0.95rem',
			}}
		>
			<span style={{ maxWidth: 640, textAlign: 'center' }}>
				{failed
					? t('We could not renew your session. Please save and sign in again.')
					: t(
							'Your session will expire in {{time}} min. Renew it to keep going.',
							{
								time: timeLabel,
							}
					  )}
			</span>

			<button
				type="button"
				className="btn btn-sm btn-warning"
				onClick={handleRenew}
				disabled={renewing}
				style={{ fontWeight: 600 }}
			>
				{renewing ? t('Renewing...') : t('Renew session')}
			</button>

			<button
				type="button"
				aria-label={t('Dismiss')}
				onClick={hide}
				style={{
					background: 'transparent',
					border: 'none',
					color: 'inherit',
					fontSize: '1.25rem',
					lineHeight: 1,
					cursor: 'pointer',
					padding: '0 0.25rem',
				}}
			>
				&times;
			</button>
		</div>
	);
};
